import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAppVersion } from "./hooks/useAppVersion";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { fetchStudentDocs } from "./utils/fetchStudentDocs";
import { ThemeProvider } from "./context/ThemeContext";
import LoginPage     from "./pages/LoginPage";
import RegisterPage  from "./pages/RegisterPage";
import HomePage      from "./pages/HomePage";
import CoursePage    from "./pages/CoursePage";
import FeesPage      from "./pages/FeesPage";
import ProfilePage   from "./pages/ProfilePage";
import LibraryPage        from "./pages/LibraryPage";
import NotificationsPage  from "./pages/NotificationsPage";
import PendingScreen from "./components/PendingScreen";
import ProfilePicker from "./components/ProfilePicker";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import "./index.css";

function Guard({ children }) {
  const [state, setState] = useState("loading");
  const [activeStudents, setActiveStudents] = useState([]);

  useEffect(() => {
    let mounted = true;

    async function check(uid, isRetry = false) {
      // Clear stored profile selection if a different auth account is now active
      const storedAuthUid = sessionStorage.getItem("activeAuthUid");
      if (storedAuthUid && storedAuthUid !== uid) {
        sessionStorage.removeItem("activeStudentId");
        sessionStorage.removeItem("activeAuthUid");
      }

      try {
        const docs = await fetchStudentDocs(uid);
        if (!mounted) return;

        if (docs.length === 0) { setState("register"); return; }

        const primary = docs.find(d => d.id === uid);
        if (primary?.status === "needs_profile") { setState("register"); return; }

        const active = docs.filter(d => d.status === "active");

        if (active.length === 0) {
          const allRejected = docs.every(d => d.status === "rejected");
          setState(allRejected ? "rejected" : "pending");
          return;
        }

        if (active.length === 1) {
          sessionStorage.setItem("activeStudentId", active[0].id);
          sessionStorage.setItem("activeAuthUid", uid);
          setState("active");
          return;
        }

        // Multiple active students — check if there's a remembered choice for this session
        const storedId = sessionStorage.getItem("activeStudentId");
        const storedFor = sessionStorage.getItem("activeAuthUid");
        if (storedId && storedFor === uid && active.some(d => d.id === storedId)) {
          setState("active");
        } else {
          setActiveStudents(active);
          setState("pick");
        }
      } catch {
        if (!mounted) return;
        if (isRetry) { setState("error"); return; }
        setTimeout(async () => {
          if (!mounted) return;
          const u = auth.currentUser;
          if (!u) { setState("unauth"); return; }
          try { await check(u.uid, true); }
          catch { if (mounted) setState("error"); }
        }, 2000);
      }
    }

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!mounted) return;
      if (!u) { setState("unauth"); return; }
      check(u.uid);
    });

    return () => { mounted = false; unsub(); };
  }, []);

  function handlePick(studentId) {
    sessionStorage.setItem("activeStudentId", studentId);
    sessionStorage.setItem("activeAuthUid", auth.currentUser?.uid || "");
    setState("active");
  }

  if (state === "loading") return <LoadingScreen />;
  if (state === "error")   return <ErrorScreen />;
  if (state === "unauth")  return <Navigate to="/login" replace />;
  if (state === "register") return <Navigate to="/register" replace />;
  if (state === "pending") return <PendingScreen />;
  if (state === "rejected") return <PendingScreen rejected />;
  if (state === "pick")    return <ProfilePicker students={activeStudents} onPick={handlePick} />;
  return children;
}

function LoadingScreen() {
  return (
    <div style={{ display: "flex", height: "100vh", alignItems: "center",
                  justifyContent: "center", background: "#FBF0E8",
                  flexDirection: "column", gap: "16px" }}>
      <div style={{ fontSize: "3rem" }}>🎵</div>
      <p style={{ color: "var(--purple-text)", fontWeight: "600", fontSize: "1rem" }}>
        Loading SurSadhana Music Classes…
      </p>
    </div>
  );
}

function ErrorScreen() {
  return (
    <div style={{ display: "flex", height: "100vh", alignItems: "center",
                  justifyContent: "center", background: "#FBF0E8",
                  flexDirection: "column", gap: "16px" }}>
      <div style={{ fontSize: "3rem" }}>🎵</div>
      <p style={{ color: "var(--purple-text)", fontWeight: "600", fontSize: "1rem" }}>
        Could not connect. Check your internet and try again.
      </p>
      <button onClick={() => window.location.reload()}
        style={{ padding: "10px 24px", background: "#7B1515", color: "white",
                 border: "none", borderRadius: "8px", cursor: "pointer",
                 fontWeight: "600", fontSize: ".95rem" }}>
        Retry
      </button>
    </div>
  );
}

function UpdateBanner() {
  const { updateAvailable, reload } = useAppVersion();
  if (!updateAvailable) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: "linear-gradient(90deg,#4E0808,#7B1515)",
      color: "white", display: "flex", alignItems: "center",
      justifyContent: "center", gap: "12px",
      padding: "10px 16px", fontSize: ".88rem", fontWeight: "600",
      boxShadow: "0 2px 12px rgba(0,0,0,.3)",
    }}>
      <span>🔄 A new version is available</span>
      <button onClick={reload} style={{
        background: "white", color: "#7B1515", border: "none",
        borderRadius: "6px", padding: "5px 14px",
        fontWeight: "800", fontSize: ".82rem",
        cursor: "pointer", whiteSpace: "nowrap",
      }}>
        Update Now
      </button>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <UpdateBanner />
        <Routes>
          <Route path="/login"    element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/privacy"  element={<PrivacyPolicyPage />} />
          <Route path="/terms"    element={<TermsPage />} />
          <Route path="/refunds"  element={<RefundPolicyPage />} />
          <Route path="/apply"    element={<Navigate to="/register" replace />} />
          <Route path="/"              element={<Guard><HomePage /></Guard>} />
          <Route path="/course/:id"    element={<Guard><CoursePage /></Guard>} />
          <Route path="/fees"          element={<Guard><FeesPage /></Guard>} />
          <Route path="/profile"       element={<Guard><ProfilePage /></Guard>} />
          <Route path="/library"        element={<Guard><LibraryPage /></Guard>} />
          <Route path="/notifications"  element={<Guard><NotificationsPage /></Guard>} />
          <Route path="*"              element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
